import styled from 'styled-components';

import { nonInputFocus } from './utils/mixins';

/**
 * Local stand-in for the Frontend-Platform `Button` atom, scoped to the sidebar.
 *
 * The component-library `Button` doesn't support the `--btn-*` CSS variable API or
 * `asChild` polymorphism the FE2.0 sidebar styles rely on, so the sidebar uses this
 * minimal base instead. Render as a link via styled-components polymorphism:
 * `<SidebarButtonBase as={Link} to="..." />` or `as="a" href="..."`.
 *
 * #### CSS Variables:
 * - `--btn-bg` / `--btn-bg-hover` / `--btn-bg-active`: background per state
 * - `--btn-text` / `--btn-text-hover` / `--btn-text-active`: text color per state
 * - `--btn-border`: border color
 *
 * #### Data attributes:
 * - `data-size`: `'medium' | 'xsmall'` (only the sizes the sidebar uses)
 * - `data-stretch`: fill the bounding element's width
 * - `data-icon-only`: square aspect ratio for icon-only buttons
 */
export const SidebarButtonBase = styled.button({
  '--btn-bg': 'var(--cl-background-secondary)',
  '--btn-bg-hover': 'var(--cl-background-secondary-hovered)',
  '--btn-text': 'var(--cl-text-default)',
  '--btn-border': 'transparent',

  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--cl-space-2)',
  flexGrow: 0,
  flexShrink: 0,

  maxHeight: '100%',
  minWidth: 'max-content',
  width: 'fit-content',
  maxWidth: '100%',

  border: '1px solid transparent',

  fontWeight: 'var(--cl-font-weight-bold)',
  textAlign: 'start',
  textDecoration: 'none',

  cursor: 'pointer',
  userSelect: 'none',
  overflow: 'clip',

  color: 'var(--btn-text)',
  backgroundColor: 'var(--btn-bg)',
  borderColor: 'var(--btn-border)',

  '& svg': {
    pointerEvents: 'none',
  },

  '&[data-stretch]': {
    width: '100%',
    minWidth: 0,
    flexShrink: 1,
  },

  '&[data-icon-only]': {
    aspectRatio: '1',
  },

  '&[disabled]': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },

  '&:not([disabled])': {
    '&:hover': {
      color: 'var(--btn-text-hover, var(--btn-text))',
      backgroundColor: 'var(--btn-bg-hover, var(--btn-bg))',
      textDecoration: 'none',
    },
    '&:active': {
      color: 'var(--btn-text-active, var(--btn-text))',
      backgroundColor: 'var(--btn-bg-active, var(--btn-bg))',
    },
  },

  '&[data-size="medium"]': {
    height: '2.5rem',
    paddingInline: 'var(--cl-space-3)',
    borderRadius: 'var(--cl-radius-2)',
    fontSize: 'var(--cl-font-size-2)',
    lineHeight: 'var(--cl-line-height-2)',
  },

  '&[data-size="xsmall"]': {
    height: '1.5rem',
    paddingInline: 'var(--cl-space-2)',
    borderRadius: 'var(--cl-radius-1)',
    fontSize: 'var(--cl-font-size-1)',
    lineHeight: 'var(--cl-line-height-1)',
  },

  ...nonInputFocus,
});
